import { pilotResponseAction } from "@/lib/pilot-access";
import { useAuthStore } from "@/stores/use-auth-store";
import { NotFound } from "./not-found";
import { useEffect } from "react";
import { Route, Routes as ReactRoutes, Navigate, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { ActivityLogs } from "@/pages/activity-logs";
import { Agreements } from "@/pages/agreements";
import { AuthLayout, ForgotPassword, PasswordReset, SignIn } from "@/pages/auth";
import { Collaborators } from "@/pages/collaborators";
import { Models, TypeModels } from "@/pages/contracts";
import { Deals, Leads, Stages } from "@/pages/crm";
import { Suspense } from "@/pages/errors";
import { FinanceAccount } from "@/pages/financial/finance-account";
import { Budgets } from "@/pages/financial/budgets";
import { FinanceCashFlow } from "@/pages/financial/finance-cash-flow";
import { FinanceCategory } from "@/pages/financial/finance-category";
import { FinanceEntries } from "@/pages/financial/finance-entries";
import { FinanceProfit } from "@/pages/financial/finance-profit";
import { Vouchers } from "@/pages/financial/vouchers";
import { Frequency } from "@/pages/frequency";
import { Layout } from "@/pages/layout";
import { PreserveWorkspaceReturn } from "@/components/team-workspace/PreserveWorkspaceReturn";
import { DataDeletionRequest, PrivacyPolicy, Terms } from "@/pages/legal";
import { MyBusiness } from "@/pages/my-business";
import { Notifications } from "@/pages/notifications";
import {
  PatientQueryRedirect,
  PatientRecordRedirect,
} from "@/pages/patients/[id]/redirects";
import { Procedures } from "@/pages/procedures";
import { Providers } from "@/pages/providers";
import {
  BirthdaysReports,
  EntriesReports,
  SchedulingsReports,
} from "@/pages/reports";
import { Roles } from "@/pages/roles";
import { Rules } from "@/pages/rules";
import {
  SettingsBillingCancel,
  SettingsBillingDowngrade,
  SettingsBillingUpgrade,
  SettingsUsage,
} from "@/pages/settings";
import { Users } from "@/pages/users";
import { Dashboard } from "@/pages/dashboard";
// C-10: the Cowork hub, the retired command centre and the SDR studio were
// deleted; the drawer (AICopilotFloating) is the only Copilot surface. The
// routes below keep redirecting so old links still land somewhere real.
// SCRUM-254: the retired URL below now lands on /centro-de-comando, the
// channel that replaced that screen, instead of the dashboard.
import { WaitlistAndNps } from "@/pages/waitlist";
import { OnboardingWizard } from "@/pages/onboarding";
import { OnboardingBilling } from "@/pages/onboarding/billing";
import { BeforeAfterGallery } from "@/pages/gallery";
import { canSeeFinancialNavigation } from "@/components/navigation/app-navigation";
import { useAuth, usePermission } from "@/hooks";
import { APP_CONSTANTS } from "@/config";
import { httpRequest } from "@/lib";

import {
  PrivateRoute,
  PublicRoute,
  RequireFeatureAccess,
  RequireAdminRole,
  RequireGrant,
  RequireOnboarding,
} from "./middlewares";
import { dispatchFeatureLimitEvent } from "@/components/common/upgrade-modal/feature-limit-event";
import { ErrorSentry } from "@/pages/test";
import { LazyRouteBoundary } from "./lazy-route-boundary";
import { lazyWithRetry } from "./lazy-with-retry";

// Each lazy page names its export, so a cache-busted retry can take the same
// export from a fresh copy of the module (WEB-CHUNK-01).
const ConversationAnalytics = lazyWithRetry(() => import("@/pages/analytics"), "ConversationAnalytics");
const AccountSettingsRedirect = lazyWithRetry(() => import("@/pages/account-settings"), "AccountSettingsRedirect");
const SettingsAccountPage = lazyWithRetry(() => import("@/pages/settings/account/settings-account-page"), "SettingsAccountPage");
const SettingsIntegrationsPage = lazyWithRetry(
  () => import("@/pages/settings/integrations/settings-integrations-page"),
  "SettingsIntegrationsPage"
);
const SettingsClinicPage = lazyWithRetry(() => import("@/pages/settings/clinic/settings-clinic-page"), "SettingsClinicPage");
const SettingsAutomationsPage = lazyWithRetry(
  () => import("@/pages/settings/automations/settings-automations-page"),
  "SettingsAutomationsPage"
);
const GrowthTeamPage = lazyWithRetry(() => import("@/pages/growth-team/GrowthTeamPage"), "GrowthTeamPage");
const Inbox = lazyWithRetry(() => import("@/pages/inbox"), "Inbox");
const Inventory = lazyWithRetry(() => import("@/pages/inventory"), "Inventory");
const PatientPage = lazyWithRetry(() => import("@/pages/patients/[id]"), "PatientPage");
const PatientPortal = lazyWithRetry(() => import("@/pages/patient-portal"), "PatientPortal");
const Patients = lazyWithRetry(() => import("@/pages/patients"), "Patients");
const Schedulings = lazyWithRetry(() => import("@/pages/schedulings"), "Schedulings");
const TelehealthRoom = lazyWithRetry(() => import("@/pages/telehealth"), "TelehealthRoom");

const ImportWizard = lazyWithRetry(() => import("@/pages/imports"), "ImportWizard");
const AttributionReport = lazyWithRetry(() => import("@/pages/reports/attribution-report"), "AttributionReport");
const DailyBriefing = lazyWithRetry(() => import("@/pages/reports/daily-briefing"), "DailyBriefing");
const CommandCenterPage = lazyWithRetry(() => import("@/pages/command-center/CommandCenterPage"), "CommandCenterPage");

type JWTDecode = {
  exp: number;
};

export function Routes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkPermission } = usePermission();

  // SCRUM-99: same access flag the sidebar uses for the financial group.
  const canViewFinancialReports = canSeeFinancialNavigation(
    user?.roles?.map((role) => role.name) ?? []
  );

  const canViewCashFlow = checkPermission("report", "view cash flow");
  const canViewSchedulingReports = checkPermission(
    "report",
    "view scheduling reports"
  );
  const canViewPatientReports = checkPermission(
    "report",
    "view patient reports"
  );

  useEffect(() => {
    const interceptor = httpRequest.interceptors.response.use(
      (response) => response,
      (error) => {
        const pilotAction = pilotResponseAction(error.response?.status, error.response?.data, useAuthStore.getState().user);
        if (pilotAction === "read-only") {
          useAuthStore.getState().setAuthState((state) => ({ user: state.user ? { ...state.user, pilot_read_only: true } : null }));
        }
        if (error.response && error.response.status === 401) {
          const auth = JSON.parse(
            localStorage.getItem(APP_CONSTANTS.AUTH_LOCAL_STORAGE_KEY) as any
          );
          if (auth) {
            const decodedToken = jwtDecode(auth.access_token) as JWTDecode;
            if (decodedToken.exp * 1000 < Date.now()) {
              localStorage.removeItem(APP_CONSTANTS.AUTH_LOCAL_STORAGE_KEY);
              localStorage.removeItem(
                APP_CONSTANTS.AUTH_USER_LOCAL_STORAGE_KEY
              );
              localStorage.removeItem(
                APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY
              );
              navigate("/auth");
            }
          }
        }
        if (
          typeof window !== "undefined" &&
          !window.location.pathname.includes("/onboarding/billing") &&
          !window.location.pathname.includes("errors") &&
          error.response &&
          pilotAction === "suspended"
        ) {
          localStorage.setItem("SUSPENSION_REDIRECT", "true");
          localStorage.removeItem(APP_CONSTANTS.AUTH_LOCAL_STORAGE_KEY);
          localStorage.removeItem(APP_CONSTANTS.AUTH_USER_LOCAL_STORAGE_KEY);
          localStorage.removeItem(
            APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY
          );
          navigate("/errors/suspense");
        }

        // SCRUM-208 (audit C-04): a 402 sends the clinic to the one surface
        // that sells a plan. The guard names the same destination, so the
        // interceptor cannot loop while the clinic is already choosing.
        if (
          typeof window !== "undefined" &&
          !window.location.pathname.includes("/onboarding/billing") &&
          error.response &&
          pilotAction === "billing"
        ) {
          navigate("/onboarding/billing");
        }

        if (
          error.response &&
          error.response.status === 429 &&
          error.response.data?.error === "Feature_limit_reached"
        ) {
          dispatchFeatureLimitEvent({
            feature: error.response.data.data?.feature ?? "",
            limit: error.response.data.data?.limit ?? 0,
            used: error.response.data.data?.used ?? 0,
            message: error.response.data.message ?? "Limite atingido.",
          });
        }

        if (
          error.response &&
          error.response.status === 403 &&
          error.response.data?.error === "Feature_not_available"
        ) {
          dispatchFeatureLimitEvent({
            feature: error.response.data.data?.feature ?? "",
            limit: 0,
            used: 0,
            message: error.response.data.message ?? "Funcionalidade não disponível no seu plano.",
          });
        }

        return Promise.reject(error);
      }
    );
    return () => httpRequest.interceptors.response.eject(interceptor);
  }, [navigate]);
  return (
    <ReactRoutes>
      <Route
        path="/auth"
        element={
          <PublicRoute>
            <AuthLayout />
          </PublicRoute>
        }
      >
        <Route path="/auth" element={<SignIn />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/new-password" element={<PasswordReset />} />
      </Route>

      <Route path="/errors">
        <Route path="/errors/suspense" element={<Suspense />} />
      </Route>

      {/* SCRUM-208 (audit C-04): one billing surface. `/pricing` was the
          marketing price list shipped inside the product bundle and `/plans`
          the legacy multi-plan catalogue; both are gone and the URLs redirect
          for one cycle, because old e-mails still carry them. */}
      <Route
        path="/pricing"
        element={<Navigate to="/onboarding/billing" replace />}
      />

      {/* Hard paywall: authed but no dashboard chrome — user lands here
          right after signup, before any subscription exists. */}
      <Route
        path="/onboarding/billing"
        element={
          <PrivateRoute>
            <OnboardingBilling />
          </PrivateRoute>
        }
      />

      <Route path="/telehealth/room" element={<LazyRouteBoundary todayAction={false}><TelehealthRoom /></LazyRouteBoundary>} />
      <Route path="/telehealth/:roomId" element={<LazyRouteBoundary todayAction={false}><TelehealthRoom /></LazyRouteBoundary>} />

      {/* Patient Portal - Public route */}
      <Route path="/p/:uuid" element={<LazyRouteBoundary todayAction={false}><PatientPortal /></LazyRouteBoundary>} />

      {/* C-16: the Sentry smoke test is a developer affordance.
          `import.meta.env.DEV` is replaced by `false` at build time, so neither
          the route nor `ErrorSentry` reaches the production bundle; the path
          answers with a redirect there instead of a blank screen. */}
      <Route path="/tests">
        <Route
          path="/tests/sentry"
          element={
            import.meta.env.DEV ? <ErrorSentry /> : <Navigate to="/" replace />
          }
        />
      </Route>
      <Route
        path="/"
        element={
          <PrivateRoute>
            <RequireOnboarding>
              <Layout />
            </RequireOnboarding>
          </PrivateRoute>
        }
      >
        <Route path="/" element={<Dashboard />} />
        {/* /cowork merged into Dashboard for the estetica launch.
            Drawer (AICopilotFloating) remains the primary Copilot surface. */}
        <Route path="/cowork" element={<Navigate to="/" replace />} />
        <Route path="/hitl-inbox" element={<AiCentralRedirect to="/growth-team/attention" />} />
        <Route path="/growth" element={<Navigate to="/growth-team" replace />} />
        <Route path="/growth-team/*" element={<GrowthTeamPage embedded />} />
        <Route path="/centro-de-comando" element={<CommandCenterPage />} />
        <Route path="/mission-control" element={<PreserveWorkspaceReturn><Navigate to="/centro-de-comando" replace /></PreserveWorkspaceReturn>} />
        <Route path="/analytics" element={<ConversationAnalytics />} />
        <Route path="/my-business" element={<MyBusiness />} />
        <Route path="/my-business/strategy" element={<MyBusiness />} />
        {/* C-08: the clinic record has one address. */}
        <Route
          path="/my-business/profile"
          element={<Navigate to="/settings/clinic" replace />}
        />
        {/* C-09: "Metas & OKRs" was a placeholder with no endpoint behind it. */}
        <Route
          path="/my-business/goals"
          element={<Navigate to="/my-business" replace />}
        />
        {/* SCRUM-208 (audit C-05): the plan is managed in settings. */}
        <Route
          path="/my-business/subscription"
          element={<Navigate to="/settings/usage" replace />}
        />
        <Route path="/dashboard/swot-wizard" element={<Navigate to="/my-business/strategy" replace />} />
        <Route
          path="/inbox"
          element={
            <Inbox />
          }
        />
        <Route path="/crm/leads" element={<Leads />} />
        <Route path="/crm/stages" element={<Stages />} />
        <Route path="/crm/deals" element={<Deals />} />
        {/* SCRUM-223: the campaign screen was removed. Reactivation is the AI
            team's follow-up work, so a bookmark lands where that work lives.
            The redirect is kept for one cycle. */}
        <Route path="/crm/campaigns" element={<Navigate replace to="/growth-team" />} />
        <Route path="/sdr/sdr-studio" element={<Navigate to="/growth-team" replace />} />
        <Route path="/sdr/settings" element={<Navigate to="/growth-team" replace />} />
        {/* SCRUM-266 (owner decision O-15, 2026-09-17): the patient journey
            screen was retired end to end. Clinics have the old URL bookmarked,
            so it lands where the automation work lives now. */}
        <Route path="/journey" element={<Navigate to="/growth-team" replace />} />
        <Route path="/waitlist" element={<WaitlistAndNps />} />
        <Route path="/onboarding" element={<OnboardingWizard />} />
        <Route path="/gallery" element={<BeforeAfterGallery />} />
        <Route
          path="/schedulings"
          element={
            <RequireGrant list="schedulings">
              <Schedulings />
            </RequireGrant>
          }
        />
        {/* One patient page (SPEC-PAC-01): the list, the page and the two
            addresses that used to open the drawer. `/patients/:id/record` and
            `/patients?patient=<id>` forward with `replace`. */}
        <Route
          path="/patients"
          element={
            <PatientQueryRedirect>
              <RequireGrant list="patients">
                <Patients />
              </RequireGrant>
            </PatientQueryRedirect>
          }
        />
        <Route path="/patients/:id" element={<PatientPage />} />
        <Route path="/patients/:id/record" element={<PatientRecordRedirect />} />
        {/* SEC-GATES-02: RequireFeatureAccess checks the plan only; the grant
            check renders the no-permission state instead of redirecting. */}
        <Route
          path="/financial/account"
          element={
            <RequireFeatureAccess
              requiredFeatures={["full_financial_module"]}
              canView={[true]}
            >
              <RequireGrant list="financeAccounts">
                <FinanceAccount />
              </RequireGrant>
            </RequireFeatureAccess>
          }
        />
        <Route
          path="/financial/account/:id/entries"
          element={
            <RequireFeatureAccess
              requiredFeatures={["full_financial_module"]}
              canView={[true]}
            >
              <RequireGrant list="financeAccounts">
                <FinanceEntries />
              </RequireGrant>
            </RequireFeatureAccess>
          }
        />
        <Route
          path="/financial/profit"
          element={
            <RequireFeatureAccess requiredFeatures={["full_financial_module"]}>
              <FinanceProfit />
            </RequireFeatureAccess>
          }
        />
        <Route
          path="/financial/category"
          element={
            <RequireFeatureAccess
              requiredFeatures={["full_financial_module"]}
              canView={[true]}
            >
              <RequireGrant list="categories">
                <FinanceCategory />
              </RequireGrant>
            </RequireFeatureAccess>
          }
        />
        <Route
          path="/financial/cash-flow"
          element={
            <RequireFeatureAccess
              requiredFeatures={["full_financial_module"]}
              canView={[canViewCashFlow]}
            >
              <FinanceCashFlow />
            </RequireFeatureAccess>
          }
        />
        <Route
          path="/financial/budgets"
          element={
            <RequireFeatureAccess
              requiredFeatures={["full_financial_module"]}
              canView={[true]}
            >
              <RequireGrant list="budgets">
                <Budgets />
              </RequireGrant>
            </RequireFeatureAccess>
          }
        />
        <Route
          path="/financial/vouchers"
          element={
            <RequireFeatureAccess
              requiredFeatures={["full_financial_module"]}
              canView={[true]}
            >
              <RequireGrant list="vouchers">
                <Vouchers />
              </RequireGrant>
            </RequireFeatureAccess>
          }
        />

        <Route path="/registrations/users" element={<Users />} />
        <Route path="/registrations/roles" element={<Roles />} />
        <Route path="/registrations/agreements" element={<Agreements />} />
        {/* C-15: `Models` had two routes. `/contracts/models` is the one the
            contract screens link to. */}
        <Route
          path="/registrations/models"
          element={<Navigate to="/contracts/models" replace />}
        />
        <Route
          path="/registrations/collaborators"
          element={
            <RequireGrant list="employees">
              <Collaborators />
            </RequireGrant>
          }
        />
        <Route
          path="/registrations/providers"
          element={
            <RequireGrant list="providers">
              <Providers />
            </RequireGrant>
          }
        />
        <Route path="/registrations/frequencies" element={<Frequency />} />
        <Route
          path="/registrations/procedures"
          element={
            <RequireGrant list="procedures">
              <Procedures />
            </RequireGrant>
          }
        />

        {/* Inventory Module */}
        <Route path="/inventory" element={<Inventory />} />

        <Route path="/contracts/type-of-models" element={<TypeModels />} />
        <Route path="/contracts/models" element={<Models />} />

        <Route
          path="/reports/schedulings"
          element={
            <RequireFeatureAccess
              requiredFeatures={["smart_reports"]}
              canView={[canViewSchedulingReports]}
            >
              <SchedulingsReports />
            </RequireFeatureAccess>
          }
        />
        <Route
          path="/reports/entries"
          element={
            <RequireFeatureAccess
              requiredFeatures={["smart_reports"]}
              canView={[canViewPatientReports]}
            >
              <EntriesReports />
            </RequireFeatureAccess>
          }
        />
        <Route
          path="/reports/birthdays"
          element={
            <RequireFeatureAccess
              requiredFeatures={["smart_reports"]}
              canView={[canViewPatientReports]}
            >
              <BirthdaysReports />
            </RequireFeatureAccess>
          }
        />
        {/* C-06: `/reports/messages` rendered a table wired to a literal empty
            response and filter controls with no handlers. Conversation volume
            is measured in `/analytics`, which reads a real endpoint. */}
        <Route
          path="/reports/messages"
          element={<Navigate to="/analytics" replace />}
        />
        {/* SCRUM-99: attribution reads are `role:admin|accountant` on the API
            (SCRUM-95). Hiding the menu item is not enough — direct navigation
            must redirect instead of rendering a screen that answers 403. */}
        {/* W2-10 (SCRUM-184): the daily briefing reads are
            `role:admin|accountant` on the API, like the other reports. */}
        <Route
          path="/relatorios/briefing"
          element={
            canViewFinancialReports ? (
              <DailyBriefing />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/relatorios/atribuicao"
          element={
            canViewFinancialReports ? (
              <AttributionReport />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* W3-03 (SCRUM-52): assisted onboarding imports, admin only. */}
        <Route
          path="/importacoes"
          element={
            <RequireAdminRole>
              <ImportWizard />
            </RequireAdminRole>
          }
        />

        <Route path="/activity-logs" element={<ActivityLogs />} />
        <Route path="/permissions" element={<Rules />} />

        {/* SCRUM-136: the five `?tab=` panels became four routed sections. */}
        <Route
          path="/account-settings"
          element={<AccountSettingsRedirect />}
        />
        <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
        <Route path="/settings/account" element={<SettingsAccountPage />} />
        <Route
          path="/settings/integrations"
          element={<SettingsIntegrationsPage />}
        />
        <Route path="/settings/clinic" element={<SettingsClinicPage />} />
        <Route
          path="/settings/automations"
          element={<SettingsAutomationsPage embedded />}
        />
        <Route path="/notifications" element={<Notifications />} />

        {/* Single-plan v2.0 — usage dashboard (SP-037). */}
        <Route path="/settings/usage" element={<SettingsUsage />} />
        <Route
          path="/settings/autonomy"
          element={
            <RequireAdminRole>
              <AiCentralRedirect to="/growth-team/rules" />
            </RequireAdminRole>
          }
        />

        {/* Single-plan v2.0 — plan lifecycle pages (SP-040). */}
        <Route
          path="/settings/billing/upgrade"
          element={<SettingsBillingUpgrade />}
        />
        <Route
          path="/settings/billing/downgrade"
          element={<SettingsBillingDowngrade />}
        />
        <Route
          path="/settings/billing/cancel"
          element={<SettingsBillingCancel />}
        />

        {/* WA-QR: the Meta Cloud page is gone — WhatsApp is connected on the
            Canais page by scanning a code, so a bookmark lands there. */}
        <Route
          path="/settings/integrations/whatsapp"
          element={<Navigate replace to="/settings/integrations" />}
        />

        {/* Legal / LGPD */}
        <Route path="/legal/privacidade" element={<PrivacyPolicy />} />
        <Route path="/legal/termos" element={<Terms />} />
        <Route
          path="/settings/exclusao-de-dados"
          element={<DataDeletionRequest />}
        />

        {/* System / Master Admin Routes */}
        <Route path="/knowledge-base" element={<AiCentralRedirect to="/growth-team/materials" />} />
      </Route>
      <Route path="/plans">
        <Route
          path="/plans"
          element={<Navigate to="/onboarding/billing" replace />}
        />
        <Route
          path="/plans/payment"
          element={<Navigate to="/onboarding/billing" replace />}
        />
      </Route>
      <Route path="*" element={<NotFound />} />
    </ReactRoutes>
  );
}

function AiCentralRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}
